import httpRequest from "@/auth/http-client";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { ImportCoverageArea } from "@/hooks/requests/coverage-area/use-import-coverage-areas";
import ICoverageArea, { ICoverageAreaInput } from "@/interfaces/coverage-area.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

type CoverageAreaApi = Omit<ICoverageArea, "freeShippingOver"> & {
  freeShippingOver?: number | string | null;
  FreeShippingOver?: number | string | null;
};

type CoverageAreaApiInput = Omit<ICoverageAreaInput, "freeShippingOver" | "customerId"> & {
  freeShippingOver?: string;
  rowDate?: string;
};

class CoverageAreaService {
  private baseUrl = "api/v1/freightzones";

  private normalizeCoverageArea(coverageArea: CoverageAreaApi): ICoverageArea {
    const { FreeShippingOver, freeShippingOver, ...rest } = coverageArea;
    const raw = freeShippingOver ?? FreeShippingOver;
    const parsed =
      raw === null || raw === undefined || raw === "" ? null : Number(raw);

    return {
      ...(rest as ICoverageArea),
      freeShippingOver: parsed,
    };
  }

  private normalizePaginatedResponse(
    response: PaginatedResponse<CoverageAreaApi>
  ): PaginatedResponse<ICoverageArea> {
    return {
      ...response,
      items: response.items.map((item) => this.normalizeCoverageArea(item)),
    };
  }

  private buildApiPayload(formData: ICoverageAreaInput): CoverageAreaApiInput {
    const { freeShippingOver, customerId, ...payload } = formData;

    return {
      ...payload,
      freeShippingOver:
        freeShippingOver === null || freeShippingOver === undefined
          ? "0"
          : Number(freeShippingOver).toFixed(2),
      rowDate: new Date().toISOString().split(".")[0],
    };
  }

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<ICoverageArea>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const coverageAreas = await httpRequest.get<PaginatedResponse<CoverageAreaApi>>(
      `${this.baseUrl}?${paginationQuery}`
    );
    return this.normalizePaginatedResponse(coverageAreas);
  }

  async filter(
    filter: IFilter<ICoverageArea>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<ICoverageArea>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const response = await httpRequest.post<PaginatedResponse<CoverageAreaApi>>(
      `${this.baseUrl}/filter?${paginationQuery}`,
      {
        filter,
      }
    );
    return this.normalizePaginatedResponse(response);
  }

  async getById(coverageAreaId: number): Promise<ICoverageArea> {
    const coverageArea = await httpRequest.get<CoverageAreaApi>(`${this.baseUrl}/${coverageAreaId}`);
    return this.normalizeCoverageArea(coverageArea);
  }

  async create(formData: ICoverageAreaInput): Promise<ICoverageArea> {
    const coverageArea = await httpRequest.post<CoverageAreaApi>(
      this.baseUrl,
      this.buildApiPayload(formData)
    );
    return this.normalizeCoverageArea(coverageArea);
  }

  async update(formData: ICoverageAreaInput, coverageAreaId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${coverageAreaId}`, this.buildApiPayload(formData));
  }

  async delete(coverageAreaId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${coverageAreaId}`);
  }

  async import(formData: ImportCoverageArea): Promise<ICoverageArea[]> {
    return await httpRequest.post(`${this.baseUrl}/import`, formData);
  }
}

const coverageAreaService = new CoverageAreaService();
export default coverageAreaService;
